import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import {
    ManualTemplateListContainer,
    ManualTemplateListItem,
    ManualTemplateListItemHeader,
    ManualTemplateListItemTitle
} from './ManualTemplateList.style';
import { Spinner } from '@interstate/components/Spinner';
import { DealTitle, InventoryTypeEnum } from './Inventorytype.enum';
import { ThemeProvider } from 'styled-components';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { DealTemplate } from '@makemydeal/dr-dash-types';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { createApplyTemplateEvent } from '../analytics/cdlEventCreators';
import { Alert } from '@interstate/components/Alert';
import { Badge } from '@interstate/components/Badge';
import { Tooltip } from '@interstate/components/Tooltip';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import { isMultiScenarioTemplate } from '../dealTemplatesDrawer/manageTemplate/utils';
import { scopedActions, scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { ApplyTemplateConfirmationModal } from './ApplyTemplateConfirmationModal';

export type ManualTemplateListVehicleCriteria = Pick<IVehicle, 'type' | 'year' | 'make' | 'model' | 'trim' | 'chromeStyleId'>;

export type ManualTemplateListProps = {
    hide?: () => void;
    selectionOnly?: boolean;
    selectedTemplateId?: string;
    onTemplateSelect?: (template: DealTemplate) => void;
    vehicleCriteria?: ManualTemplateListVehicleCriteria;
};

export const ManualTemplateList = ({
    hide,
    selectionOnly = false,
    selectedTemplateId,
    onTemplateSelect,
    vehicleCriteria
}: ManualTemplateListProps) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();

    const dealTemplateList = useSelector(dealTemplateSelectors.getManualDealTemplateList) || [];
    const isLoading = useSelector(dealTemplateSelectors.getIsManualDealTemplateListLoading);
    const dealVehicle = useSelector(offerReduxSelectors.getVehicleDetails);

    const { type, make, year, model, trim, chromeStyleId } = vehicleCriteria ?? dealVehicle;
    const hasMultipleVehicles = useSelector(dealTemplateSelectors.getHasMultipleVehiclesAcrossScenarios);
    const hasMultipleScenarios = useSelector(scopedStateSelectors.hasMultipleActiveScopes);
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const [templateToManuallyApply, setTemplateToManuallyApply] = useState<DealTemplate | null>(null);
    const [templatePendingConfirmation, setTemplatePendingConfirmation] = useState<DealTemplate | null>(null);

    const inventoryType = InventoryTypeEnum[type?.toLowerCase() as keyof typeof InventoryTypeEnum];

    const hasDisabledMultiScenarioTemplates = hasMultipleVehicles && dealTemplateList.some(isMultiScenarioTemplate);

    const templatesOfAllType = useMemo(() => {
        return dealTemplateList
            .filter((template) => {
                return template.inventoryTypes === null;
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [dealTemplateList]);

    const templatesOfSpecifiedType = useMemo(() => {
        return dealTemplateList
            .filter((template) => {
                return template.inventoryTypes?.includes(inventoryType) || false;
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [dealTemplateList, inventoryType]);

    const isTemplateDisabled = useCallback(
        (template: DealTemplate): boolean => isMultiScenarioTemplate(template) && hasMultipleVehicles,
        [hasMultipleVehicles]
    );

    const handleManualApply = useCallback(
        (template: DealTemplate) => {
            const valueCheck = (val?: unknown[] | unknown) => (Array.isArray(val) ? !!val.length : !!val);

            const eventPayload = {
                inventoryType: valueCheck(template.inventoryTypes),
                year: valueCheck(template.years),
                make: valueCheck(template.makes),
                model: valueCheck(template.models),
                trim: valueCheck(template.trims),
                configuration: valueCheck(template.configurations)
            };
            const isMultiScenario = isMultiScenarioTemplate(template);
            const multiScenarioText = isMultiScenario ? 'Multi-Scenario' : undefined;
            const analyticsEvent = createApplyTemplateEvent(eventPayload, multiScenarioText);
            dispatch(analyticsEvent);

            const applyAction = dealTemplateActionCreators.manuallyApplyDealTemplate(template.id, template.name);
            dispatch(isMultiScenario ? scopedActions.createScopedAction(primaryScopeId, applyAction) : applyAction);
            hide?.();
        },
        [dispatch, hide, primaryScopeId]
    );

    useEffect(() => {
        if (templateToManuallyApply) {
            handleManualApply(templateToManuallyApply);
        }
    }, [templateToManuallyApply]);

    const handleTemplateSelected = useCallback(
        (template: DealTemplate) => {
            if (selectionOnly) {
                onTemplateSelect?.(template);
                return;
            }
            if (hasMultipleScenarios && isMultiScenarioTemplate(template)) {
                setTemplatePendingConfirmation(template);
            } else {
                setTemplateToManuallyApply(template);
            }
        },
        [hasMultipleScenarios, selectionOnly, onTemplateSelect]
    );

    const handleTemplateKeyDown = useCallback(
        (evt: React.KeyboardEvent, template: DealTemplate) => {
            if (evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault();
                handleTemplateSelected(template);
            }
        },
        [handleTemplateSelected]
    );

    const handleConfirmApply = useCallback(() => {
        setTemplateToManuallyApply(templatePendingConfirmation);
        setTemplatePendingConfirmation(null);
    }, [templatePendingConfirmation]);

    const handleCancelApply = useCallback(() => {
        setTemplatePendingConfirmation(null);
    }, []);

    useEffect(() => {
        dispatch(
            dealTemplateActionCreators.getManualDealTemplates({
                ...(inventoryType && { inventoryType }),
                ...(year && { year: `${year}` }),
                ...(make && { make }),
                ...(model && { model }),
                ...(trim && { trim }),
                ...(chromeStyleId && { configuration: `${chromeStyleId}` })
            })
        );
    }, [dispatch, inventoryType, year, make, model, trim, chromeStyleId]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '400px'
                }}
            >
                <Spinner />
            </Box>
        );
    }

    const renderTemplateItem = (template: DealTemplate) => {
        const disabled = isTemplateDisabled(template);
        const isSelected = selectionOnly && selectedTemplateId === template.id;
        return (
            <ManualTemplateListItem
                data-testid={`template-list-item-${template.id}`}
                onClick={disabled ? undefined : () => handleTemplateSelected(template)}
                onKeyDown={selectionOnly && !disabled ? (evt) => handleTemplateKeyDown(evt, template) : undefined}
                key={template.id}
                role={selectionOnly ? 'option' : undefined}
                aria-selected={selectionOnly ? isSelected : undefined}
                aria-disabled={selectionOnly ? disabled : undefined}
                tabIndex={selectionOnly && !disabled ? 0 : undefined}
                $selected={isSelected}
                style={disabled ? { cursor: 'default' } : undefined}
            >
                <ManualTemplateListItemHeader>
                    <Box display="flex" alignItems="center" gap="8px">
                        <Typography variant="strong-md" color={disabled ? 'base.color.gray.500' : 'base.color.blue.700'}>
                            {template.name}
                        </Typography>
                        {disabled && (
                            <Tooltip
                                data-testid={`multi-scenario-tooltip-${template.id}`}
                                position="top-start"
                                size="large"
                                toolTipContent="Template application is not available for a deal with different vehicles. Update scenarios to continue."
                            >
                                <InformationCircleIcon
                                    color={theme.tokens.BaseColorBlue700}
                                    style={{
                                        transform: 'translateY(0.15rem)'
                                    }}
                                    fontSize="large"
                                    data-testid={`multi-scenario-info-icon-${template.id}`}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    {isMultiScenarioTemplate(template) && <Badge variant="light">Multi-scenario</Badge>}
                </ManualTemplateListItemHeader>
                <Typography variant="label-sm">{template.description}</Typography>
            </ManualTemplateListItem>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <Box>
                {dealTemplateList.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {!selectionOnly && <Typography variant="body-sm">Select a template to apply on your deal.</Typography>}
                        {hasDisabledMultiScenarioTemplates && (
                            <Alert
                                type="info"
                                data-testid="multi-scenario-template-alert"
                                title="Multi-scenario templates unavailable"
                            >
                                Multi-scenario templates cannot be applied when deal scenarios contain different vehicles.
                            </Alert>
                        )}
                        <ManualTemplateListContainer
                            role={selectionOnly ? 'listbox' : undefined}
                            aria-label={selectionOnly ? 'Deal templates' : undefined}
                        >
                            {!!templatesOfAllType.length && (
                                <>
                                    <ManualTemplateListItemTitle role={selectionOnly ? 'presentation' : undefined}>
                                        <Typography variant="body-sm" color="base.color.gray.600">
                                            {DealTitle['All']}
                                        </Typography>
                                    </ManualTemplateListItemTitle>
                                    {templatesOfAllType.map(renderTemplateItem)}
                                </>
                            )}
                            {!!templatesOfSpecifiedType.length && (
                                <>
                                    <ManualTemplateListItemTitle role={selectionOnly ? 'presentation' : undefined}>
                                        <Typography variant="body-sm" color="base.color.gray.600">
                                            {DealTitle[inventoryType]}
                                        </Typography>
                                    </ManualTemplateListItemTitle>
                                    {templatesOfSpecifiedType.map(renderTemplateItem)}
                                </>
                            )}
                        </ManualTemplateListContainer>
                    </Box>
                ) : (
                    <Typography variant="body-sm">This section is empty—no templates have been added yet.</Typography>
                )}
            </Box>
            {templatePendingConfirmation && (
                <ApplyTemplateConfirmationModal show onConfirm={handleConfirmApply} onCancel={handleCancelApply} />
            )}
        </ThemeProvider>
    );
};
